<template>
  <div class="property-form-page">
    <div class="d-flex justify-content-between align-items-center flex-wrap mb-2">
      <div>
        <div class="d-flex align-items-center" style="gap: .5rem">
          <h4 class="mb-0">{{ id ? 'Edit Property' : 'Add New Property' }}</h4>
          <b-badge
            v-if="checkPropertyData"
            pill
            :variant="isApproved ? 'success' : 'warning'"
          >
            {{ isApproved ? 'Approved' : 'Pending Approval' }}
          </b-badge>
        </div>
        <small class="text-muted" v-if="id">Property ID #{{ id }}</small>
        <small class="text-muted" v-else>Fill in the details below to list a new property</small>
      </div>

      <div
        v-if="id"
        class="header-actions d-flex flex-column flex-md-row justify-content-md-end mt-1 mt-md-0"
        style="gap: .5rem; width: 100%; max-width: 600px;"
      >
        <b-button
          variant="outline-primary"
          :to="{ name: 'property-upload', params: { id: id } }"
        >
          <feather-icon icon="ImageIcon" class="mr-50" />Upload Images
        </b-button>

        <b-button
          variant="outline-primary"
          :to="{ name: 'property-tax', params: { id: id } }"
        >
          <feather-icon icon="FileTextIcon" class="mr-50" />Tax & Title Deed
        </b-button>

        <b-button
          v-if="isApproved"
          variant="primary"
          :to="{ name: 'inventory-add', query: { propertyId: id } }"
        >
          <feather-icon icon="ListIcon" class="mr-50" />Inventory List
        </b-button>
      </div>
    </div>

    <validation-observer ref="simpleRules">
      <b-form>
        <b-row>
          <b-col lg="6">
            <form-info
              v-on:childToParent="onChildClick" ref="formInfo"
              :id="id" :parentData="propertyData" />
          </b-col>

          <b-col lg="6">
            <form-owner v-on:childToParent="onChildClick" :id="id" ref="formOwner"/>
            <form-sale v-on:childToParent="onSaleChange" ref="formSale"/>
            <form-facility v-on:childToParent="onFacilityCheck" :id="id" />
            <form-comment v-if="id" :id="id" v-on:commentAdded="onCommentAdded" />
          </b-col>
        </b-row>

        <b-card class="form-actions-bar mb-0 shadow-sm">
          <div class="d-flex justify-content-end flex-wrap" style="gap: .75rem">
            <b-button
              variant="primary"
              type="submit"
              @click.prevent="openConfirm(false)"
            >
              {{ checkPropertyData && checkPropertyData.is_approve != 1 ? 'Approve' : 'Save' }}
            </b-button>

            <b-button
              v-if="id"
              variant="outline-primary"
              @click.prevent="openConfirm(true)"
            >
              <feather-icon icon="FacebookIcon" class="mr-50" />
              Save and Post
            </b-button>
          </div>
        </b-card>
      </b-form>
    </validation-observer>

    <b-modal
      id="confirm-save-modal"
      ref="confirmSaveModal"
      title="Confirm Save"
      ok-title="Yes, Active"
      cancel-title="Inactive"
      centered
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      @ok="confirmSave"
      @cancel="handleCancel"
    >
      <div class="text-center py-2">
        <p class="mb-0">
          {{ postAfterSave? 'Save and post this property to Facebook?'
            : 'Status or price has been changed. Activate this property now?' }}
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { ref, onUnmounted } from '@vue/composition-api'
import router from '@/router'
import FormInfo from './FormInfo.vue'
import FormOwner from './FormOwner.vue'
import FormSale from './FormSale.vue'
import FormFacility from './FormFacility.vue'
import FormComment from './FormComment.vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormInput, BFormGroup, BForm, BRow, BCol, BButton, BFormCheckbox, BOverlay, BCard, BBadge
} from 'bootstrap-vue'
import BCardActions from '../../../@core/components/b-card-actions/BCardActions.vue'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import propertyStoreModule from '../propertiesStoreModule'

export default {
  components: {
    BCardActions,
    FormInfo,
    FormOwner,
    FormSale,
    FormFacility,
    ValidationProvider,
    ValidationObserver,
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    BFormCheckbox,
    FormComment,
    BOverlay,
    BCard,
    BBadge
  },
  data() {
    return {
      input : {},
      inputChange:{},
      id: router.currentRoute.params.id,
      userData: JSON.parse(localStorage.getItem('userData')),
      blur: '2px',
      show: false,
      variant: 'light',
      opacity: 0.85,
      postAfterSave: false,
    }
  },
  directives: {
    Ripple,
  },
  setup() {
    const PROPERTY_APP_STORE_MODULE_NAME = 'app-property'
    if (!store.hasModule(PROPERTY_APP_STORE_MODULE_NAME)) store.registerModule(PROPERTY_APP_STORE_MODULE_NAME, propertyStoreModule)
    onUnmounted(() => {
      if (store.hasModule(PROPERTY_APP_STORE_MODULE_NAME)) store.unregisterModule(PROPERTY_APP_STORE_MODULE_NAME)
    })

    const propertyData = ref(null)
    const inputdata = ref(null)
    let id = router.currentRoute.params.id
    const checkPropertyData = ref(null)

    const fetchPropertyData = async() => {
      if(id){
        const { data } = await axios.get(`property/v1/getdetail/${id}`);

        if(data?.data?.length){
          checkPropertyData.value = data.data[0]
        }
      }
    }

    fetchPropertyData()
   
    return {
      propertyData,
      inputdata,
      checkPropertyData,
    }
  },
  computed: {
    isApproved() {
      return !!(this.checkPropertyData && this.checkPropertyData.is_approve == 1)
    },
  },
  methods: {
    onCommentAdded() {},
    onSaleChange(value) {
      this.input = Object.assign({}, this.input, value)

      // ราคาอยู่ใน FormSale แต่ช่อง Action อยู่ใน FormInfo — ส่งต่อให้ FormInfo ตัดสินเอง
      if (this.$refs.formInfo) {
        this.$refs.formInfo.syncActionWithPrices(
          Number(this.input.salePrice) > 0,
          Number(this.input.rentPrice) > 0,
        )
      }
    },
    onChildClick(value){
      this.input =  Object.assign({}, this.input, value );
      this.inputChange = this.input;
    },
    onFacilityCheck(value){
      this.input =  Object.assign({}, this.input, value);
    },
    handleCancel() {
      this.validationForm(this.postAfterSave)
    },
    scrollToFirstError() {
      this.$nextTick(() => {
        const firstError = this.$el.querySelector('.is-invalid')
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    async openConfirm(post) {
      const isValid = await this.$refs.formInfo.validateForm()
      const isOwnerValid = await this.$refs.formOwner.validateForm()
      const isSaleValid = this.$refs.formSale.validateForm()

      if (!isValid || !isOwnerValid || !isSaleValid) {
        this.showToast('danger', 'AlertTriangleIcon', 'Error', "Please fill required fields")
        this.scrollToFirstError()
        return
      }

      this.postAfterSave = post

      const payloads = {
        property_id: this.id,
        action: this.input.action,
        salePrice: this.input.salePrice,
        rentPrice: this.input.rentPrice
      }

      try {
        const { data } = await axios.post(`property/v2/status-price/check`, payloads)

        if(data?.success) {
          const hasChage = data.data

          if(hasChage && !this.input.is_active) {
            this.$refs.confirmSaveModal.show()
          } else {
            this.validationForm(this.postAfterSave)
          }
        }
      } catch(error) {
        const errMsg = error?.response?.data?.message || 'Something went wrong!'
        this.showToast('danger', 'AlertTriangleIcon', 'Error', errMsg)
      }
    },
    async confirmSave() {
      this.input.is_active = true
      if (this.$refs.formInfo) {
        this.$refs.formInfo.setIsActive(true)
      }
      await this.validationForm(this.postAfterSave)
    },
    async validationForm (post) {
      if (this.$refs.formInfo) {
        const formInfo = this.$refs.formInfo
        const currentDate = formInfo.input.available_date || ''
        const originalDate = formInfo.originalAvailableDate || ''
        if (!currentDate || currentDate === originalDate) {
          formInfo.setAvailableDateToToday()
        }
      }

      this.input.created_by = this.userData.id;

      if(this.id && this.id != undefined){
        this.input.id = this.id
        
        try {
          const { data } = await axios.post(`property/v2/update`, this.input)

          if(data?.success) {
            this.showToast('success', 'CoffeeIcon', 'Done', data.message)
            if(post) {
              this.postToSocial()
            }
            if (this.$refs.formInfo) {
              await this.$refs.formInfo.getData()
            }
          }
        } catch(error) {
          const errMsg = error?.response?.data?.message || 'Something went wrong!'
          this.showToast('danger', 'AlertTriangleIcon', 'Error', errMsg)
        }
      } else {
        try {
          const { data } = await axios.post(`property/v2/add`, this.input)

          if(data?.success) {
            const propertyId = data.data.property_id || ''
            this.$router.push(`/property/upload/${propertyId}`);
          }
        } catch(error) {
          const errMsg = error?.response?.data?.message || 'Something went wrong!'
          this.showToast('danger', 'AlertTriangleIcon', 'Error', errMsg)
        }
      }
    },
    async postToSocial(){
      try {
        const propertyId = this.id

        const response = await store.dispatch('app-property/facebookPost', propertyId);

        if(response.data.success) {
          this.showToast('success', 'CoffeeIcon', 'Done', response.data.message)
        }
      } catch(error) {
        const message =
          error.response?.data?.message || error.message || 'Something went wrong';

        this.showToast('danger', 'AlertTriangleIcon', 'Error', message)
      }
    },
    showToast(variant, icon, title, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
            title: title,
            text: text,
            icon: icon,
            variant: variant,
        },
      })
    }
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.property-form-page {
  .form-actions-bar {
    position: sticky;
    bottom: 0;
    z-index: 5;
    border-radius: 0.5rem;
  }

  .header-actions .btn {
    width: 100%;
  }

  @media (min-width: 768px) {
    .header-actions .btn {
      width: auto;
    }
  }
}
</style>